import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { BadgeCheck, Search } from "lucide-react";

/**
 * Handle input with debounced HLL Records suggestions. Selecting a suggestion
 * calls `onPick({handle, player_id, hll_profile_url, hll_verified})`.
 * Reps stop typing 17-digit Steam IDs.
 */
export default function RosterHandleSearch({
  value,
  onChange,
  onPick,
  placeholder = "ZeroFrost",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = (value || "").trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/hll-records/search?q=${encodeURIComponent(q)}`);
        setResults(Array.isArray(data) ? data : []);
        setHi(0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (r) => {
    onPick?.(r);
    setOpen(false);
    setResults([]);
  };

  const showList = open && (loading || results.length > 0);

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
        <input
          data-testid={testId}
          value={value}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (!showList) return;
            if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(results.length - 1, h + 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
            else if (e.key === "Enter" && results[hi]) { e.preventDefault(); pick(results[hi]); }
            else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); }
          }}
          placeholder={placeholder}
          className="w-full h-9 pl-7 pr-2 rounded-none bg-[#05050A] border border-[#232330] font-mono text-sm text-white focus:outline-none focus:border-cyan-400"
          autoComplete="off"
        />
      </div>
      {showList && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-[#0a0a12] border border-cyan-400/60 shadow-[0_0_20px_rgba(0,240,255,0.25)] max-h-64 overflow-y-auto"
        >
          {loading && (
            <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-gray-500">
              Searching HLL Records…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-gray-500">
              No matches — keep typing to add a fresh entry.
            </div>
          )}
          {!loading && results.map((r, i) => (
            <button
              key={`${r.handle}-${r.player_id}-${i}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setHi(i)}
              data-testid={`${testId}-opt-${i}`}
              className={`w-full text-left px-3 py-2 border-b border-[#232330] last:border-b-0 transition-colors ${
                i === hi ? "bg-cyan-400/10" : "hover:bg-cyan-400/5"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading font-bold uppercase tracking-tight text-sm text-white truncate">
                  {r.handle}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.hll_verified && (
                    <span className="text-[9px] font-mono uppercase tracking-widest text-cyan-400 flex items-center gap-1">
                      <BadgeCheck className="w-3 h-3" /> verified
                    </span>
                  )}
                  {r.team_tag && (
                    <span className="text-[9px] font-mono uppercase tracking-widest text-[#F4D03F]">
                      {r.team_tag}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[10px] font-mono tracking-widest text-gray-500 truncate mt-0.5">
                {r.player_id || "no id on record"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
